import { Coordinate, LatLngTuple, RenderRouteType, RouteTransportTypes, RouteType } from "@qubictron/maplib";
import { RouteDataType, TransportTypes } from "shared/api/lib/map.types";
import { isNullable, Nullable, ValueOf } from "shared/lib/typeCheck.lib";

type LocationModel = {
  id?: number;
  name?: Nullable<string>;
  country?: Nullable<string>;
  countryCode?: Nullable<string>;
  locode?: Nullable<string>;
  lat?: Nullable<number>;
  lng?: Nullable<number>;
};

type RawLatLngTuple = [Nullable<number>, Nullable<number>];

type RouteData = {
  path: Nullable<RawLatLngTuple[]>;
  type: Nullable<ValueOf<typeof RouteDataType>>;
  transportType: Nullable<ValueOf<typeof TransportTypes>>;
};

export type MapRouteModel = {
  route?: Nullable<RouteData[]>;
  locations?: Nullable<LocationModel[]>;
  currentPin?: Coordinate;
};

export type PointType = {
  id: string;
  coordinate: Coordinate;
  label?: string;
  isCurrentPin?: boolean;
  transportType?: ValueOf<typeof RouteTransportTypes>;
};

export type RouteReturnType = {
  points: PointType[];
  route: RenderRouteType;
  deflectionAngle?: number;
};

export type GetDeflectionAngleParams = {
  route: Coordinate[];
  currentLatLng: Coordinate | null;
};

const TransportTypeMap: Record<ValueOf<typeof TransportTypes>, ValueOf<typeof RouteTransportTypes>> = {
  [TransportTypes.AIR]: RouteTransportTypes.AIR,
  [TransportTypes.VESSEL]: RouteTransportTypes.SEA,
  [TransportTypes.BARGE]: RouteTransportTypes.SEA,
  [TransportTypes.FEEDER]: RouteTransportTypes.SEA,
  [TransportTypes.TRUCK]: RouteTransportTypes.ROAD,
  [TransportTypes.RAIL]: RouteTransportTypes.RAIL,
  [TransportTypes.PLANE]: RouteTransportTypes.AIR,
  [TransportTypes.PARCEL]: RouteTransportTypes.OTHER,
};

export class MapRoute {
  private routeModel: MapRouteModel;
  private fullRoute: RouteType[] = [];
  currentPin?: Coordinate;
  deflectionAngle?: number;

  renderRoute: Nullable<RouteReturnType> = null;
  routePoints: PointType[] = [];

  constructor(data: MapRouteModel) {
    this.routeModel = data;
    this.currentPin = data.currentPin;

    this.setFullRoute();
    this.setRoutePoints();
    this.setRenderRoute();

    if (this.currentPin) {
      this.deflectionAngle = this.getDeflectionAngle({
        route: this.fullRoute.flatMap((segment) => segment.coordinates),
        currentLatLng: this.currentPin,
      });
    }
  }

  setFullRoute() {
    const routeData = this.routeModel?.route?.flatMap(this.normalizeRoute);

    if (!this.routeModel || !routeData?.length) {
      this.fullRoute = [];
      return;
    }

    this.fullRoute = routeData.map((routeSegment) => ({
      coordinates: routeSegment.path.map((point) => ({ lng: point[1], lat: point[0] })),
      transportType: TransportTypeMap[routeSegment.transportType],
    }));
  }

  private setRenderRoute(): void {
    this.renderRoute = {
      points: this.routePoints,
      route: { route: this.fullRoute, currentPin: this.currentPin },
    };
  }

  private setRoutePoints(): void {
    const segments = this.fullRoute;

    const coords = segments.flatMap((segment, segmentIndex) => {
      const path = segment.coordinates;
      if (!path.length) return [];

      const toItem = (p: (typeof path)[0]) => ({ point: p, segmentIndex });
      const isLastSegment = segmentIndex === segments.length - 1;

      const items = [toItem(path[0])];
      if (isLastSegment && path.length > 1) {
        items.push(toItem(path[path.length - 1]));
      }
      return items;
    });

    this.routePoints = coords.map(({ point, segmentIndex }, index) => ({
      id: `${index}-${point.lng}-${point.lat}`,
      coordinate: { lng: point.lng, lat: point.lat },
      label: this.getPointName({ lat: point.lat, lng: point.lng }),
      transportType: segments[segmentIndex].transportType,
    }));

    if (this.currentPin) {
      this.routePoints.push({
        id: `currentPin-${this.currentPin.lng}-${this.currentPin.lat}`,
        coordinate: { lng: this.currentPin.lng, lat: this.currentPin.lat },
        isCurrentPin: true,
      });
    }
  }

  private getPointName(pin: Coordinate, isSecondCall?: boolean): string {
    const threshold = 0.5;
    const locations = this.routeModel?.locations;
    if (!locations) return "";

    let closestLocation: (typeof locations)[number] | undefined;
    let closestMargin = Infinity;

    locations.forEach((location) => {
      if (!isNullable(location.lat) && !isNullable(location.lng)) {
        const margin = Math.max(Math.abs(location.lat - pin.lat), Math.abs(location.lng - pin.lng));
        if (margin <= threshold && margin < closestMargin) {
          closestMargin = margin;
          closestLocation = location;
        }
      }
    });

    if (!closestLocation) {
      if (!isSecondCall) {
        return this.getPointName(this.getAntimeridianFixedLatLng(pin.lat, pin.lng), true);
      }
      return "";
    }
    const name = closestLocation.name ?? "";

    const countryCode = closestLocation.countryCode ? `, ${closestLocation.countryCode}` : "";
    return `${name}${countryCode}`;
  }

  private getAntimeridianFixedLatLng(lat: number, lng: number): Coordinate {
    if (lng > 0) {
      return { lat: lat, lng: lng + -360 };
    }
    if (lng < 0) {
      return { lat: lat, lng: lng + 360 };
    }
    return { lat: lat, lng: lng };
  }

  private normalizeRoute = (route: RouteData) => {
    if (isNullable(route.path) || isNullable(route.type) || isNullable(route.transportType)) {
      if (__DEV__) {
        console.error("Invalid route data", route);
      }
      return [];
    }

    const path = route.path.reduce<LatLngTuple[]>((acc, point) => {
      if (!isNullable(point[0]) && !isNullable(point[1])) {
        acc.push([Number(point[0]), Number(point[1])]);
      }
      return acc;
    }, []);

    if (!path.length) {
      if (__DEV__) {
        console.error("Invalid path data", path);
      }
      return [];
    }

    return [{ path, type: route.type, transportType: route.transportType }];
  };

  private degToRad = (degrees: number): number => {
    return degrees * (Math.PI / 180);
  };

  private radToDeg = (radians: number): number => {
    return radians * (180 / Math.PI);
  };

  private getAntimeridianFixedCoordinate = (lat: number, lng: number): Coordinate => {
    if (lng > 0) {
      return { lat, lng: lng + -360 };
    }
    if (lng < 0) {
      return { lat, lng: lng + 360 };
    }
    return { lat, lng };
  };

  private getCurrentPinIndex = (fullRoute: Coordinate[], currentLatLng: Coordinate): number => {
    const currentPinIndex = (currentPin: Coordinate) =>
      fullRoute.findIndex((el) => el.lat === currentPin.lat && el.lng === currentPin.lng);
    if (currentPinIndex(currentLatLng) >= 0) return currentPinIndex(currentLatLng);
    return currentPinIndex(this.getAntimeridianFixedCoordinate(currentLatLng.lat, currentLatLng.lng));
  };

  private calculateAzimuth = (latA: number, lngA: number, latB: number, lngB: number): number => {
    if (!latA || !lngA || !latB || !lngB) return 0;
    const deltaLng = lngB - lngA;
    const y = Math.sin(this.degToRad(deltaLng)) * Math.cos(this.degToRad(latB));
    const x =
      Math.cos(this.degToRad(latA)) * Math.sin(this.degToRad(latB)) -
      Math.sin(this.degToRad(latA)) * Math.cos(this.degToRad(latB)) * Math.cos(this.degToRad(deltaLng));
    const azimuthRad = Math.atan2(y, x);
    let azimuthDeg = this.radToDeg(azimuthRad);

    azimuthDeg = (azimuthDeg + 360) % 360;
    const iconDefaultDirection = 90;
    return azimuthDeg > 0 ? azimuthDeg - iconDefaultDirection : azimuthDeg + iconDefaultDirection;
  };

  private getDeflectionAngle = ({ route, currentLatLng }: GetDeflectionAngleParams): number => {
    if (!currentLatLng) return 0;

    const currentPinWithNearestPoint: Coordinate[] = [];
    const currentPinIndex = this.getCurrentPinIndex(route, currentLatLng);
    const prevCurrentPinIndex = 1;
    const nextCurrentPinIndex = 1;
    const firstPointInFullRouteIndex = 0;
    // if currentPin found //refactor
    if (currentPinIndex >= 0) {
      // if next pin after currentPin exist
      if (route[currentPinIndex + nextCurrentPinIndex]) {
        // if status arrived but currentPin isn't last pin
        if (currentPinIndex === route.length - 2) {
          const lastPointIndex = route[route.length - 1]; // 1 - point after currentPin
          const prevCurrentPin = route[route.length - 3]; // 3 - point before currentPin
          const prevAzimut = this.calculateAzimuth(
            prevCurrentPin.lat,
            prevCurrentPin.lng,
            route[currentPinIndex].lat,
            route[currentPinIndex].lng
          );
          const nextAzimut = this.calculateAzimuth(
            route[currentPinIndex].lat,
            route[currentPinIndex].lng,
            lastPointIndex.lat,
            lastPointIndex.lng
          );
          // if equals azimuts
          if (prevAzimut !== nextAzimut) {
            currentPinWithNearestPoint.push(prevCurrentPin, lastPointIndex);
          } else {
            // if azimuts equals, we will use currentPin and next pin
            currentPinWithNearestPoint.push(route[currentPinIndex], route[currentPinIndex + nextCurrentPinIndex]);
          }
        } else if (currentPinIndex === firstPointInFullRouteIndex) {
          // if current pin is first point
          currentPinWithNearestPoint.push(route[currentPinIndex], route[currentPinIndex + nextCurrentPinIndex * 2]);
        } else {
          // if next pin after currentPin exist and currentPin not last point
          currentPinWithNearestPoint.push(route[currentPinIndex], route[currentPinIndex + nextCurrentPinIndex]);
        }
      } else {
        // if next pin after currentPin not exist, we will use prev pins (-2)
        if (route[currentPinIndex - nextCurrentPinIndex - 2]) {
          currentPinWithNearestPoint.push(route[currentPinIndex - prevCurrentPinIndex - 2], route[currentPinIndex]);
        } else {
          // if prev pins (-2) before currentPin not exist, we will use prev pins (-1)
          currentPinWithNearestPoint.push(route[currentPinIndex - prevCurrentPinIndex], route[currentPinIndex]);
        }
      }
    }

    if (!currentPinWithNearestPoint[1] || !currentPinWithNearestPoint[0]) return 0;

    return this.calculateAzimuth(
      currentPinWithNearestPoint[0].lat,
      currentPinWithNearestPoint[0].lng,
      currentPinWithNearestPoint[1].lat,
      currentPinWithNearestPoint[1].lng
    );
  };
}
